import React, { useState, useMemo } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Plus, X, Wrench } from "lucide-react";
import { fmtRoDate } from "../../lib/dateFmt";

const KIND_META = {
  project:   { label: "📁 Proiect",   chip: "bg-blue-100 text-blue-800 border-blue-300" },
  regie:     { label: "🛠 Regie",     chip: "bg-amber-100 text-amber-800 border-amber-300" },
  magazie:   { label: "🏭 Magazie",   chip: "bg-sky-100 text-sky-800 border-sky-300" },
  dismantle: { label: "🔧 Demontaj",  chip: "bg-orange-100 text-orange-800 border-orange-300" },
  other:     { label: "✍ Altul",     chip: "bg-slate-100 text-slate-800 border-slate-300" },
};
const KIND_KEYS = ["project", "regie", "dismantle", "magazie", "other"];

let _rid = 0;
const newRid = () => `r${++_rid}`;

function makeRowFromCalEntry(c) {
  const kind = c.kind || (c.is_dismantle ? "dismantle" : (c.project_id ? "project" : (c.regie_id ? "regie" : "other")));
  return {
    rid: newRid(),
    kind,
    project_id: c.project_id || "",
    regie_id: c.regie_id || "",
    custom_label: kind === "other" ? (c.note || "").replace(/^\[.*?\]\s*/, "") || "" : "",
    hours: c.hours ?? "",
    hours_driven: c.hours_driven ?? "",
    note: c.note || "",
  };
}

export function LogHoursDialog({ person_id, date, name, projects, regii, existingDay, onClose, onSaved }) {
  const [fetchedDay, setFetchedDay] = React.useState(existingDay || null);
  React.useEffect(() => {
    let cancelled = false;
    if (existingDay !== undefined && existingDay !== null) {
      setFetchedDay(existingDay);
      return;
    }
    // fetch existing entries for this person+date
    const month = (date || "").slice(0, 7);
    if (!month || !person_id) return;
    api.get(`/persons/${person_id}/calendar?month=${month}`)
      .then((r) => {
        if (cancelled) return;
        const list = (r.data || []).filter((e) => e.date === date);
        setFetchedDay(list);
      })
      .catch(() => { if (!cancelled) setFetchedDay([]); });
    return () => { cancelled = true; };
  }, [person_id, date, existingDay]);

  const initialRows = useMemo(() => {
    if (Array.isArray(fetchedDay) && fetchedDay.length > 0) {
      return fetchedDay.map(makeRowFromCalEntry);
    }
    return [{ rid: newRid(), kind: "project", project_id: "", regie_id: "", custom_label: "", hours: "", hours_driven: "", note: "" }];
  }, [fetchedDay]);
  const [rows, setRows] = useState(initialRows);
  // Re-init rows when fetchedDay completes
  React.useEffect(() => { setRows(initialRows); /* eslint-disable-next-line */ }, [fetchedDay]);
  const [busy, setBusy] = useState(false);

  const totalHours = useMemo(() => rows.reduce((s, r) => s + (parseFloat(r.hours) || 0), 0), [rows]);
  const totalDriven = useMemo(() => rows.reduce((s, r) => s + (parseFloat(r.hours_driven) || 0), 0), [rows]);

  const updateRow = (rid, field, value) => {
    setRows((prev) => prev.map((r) => r.rid === rid ? { ...r, [field]: value } : r));
  };

  const addRow = (kind = "project") => {
    setRows((prev) => [...prev, { rid: newRid(), kind, project_id: "", regie_id: "", custom_label: "", hours: "", hours_driven: "", note: "" }]);
  };
  const removeRow = (rid) => setRows((prev) => prev.length === 1 ? prev : prev.filter((r) => r.rid !== rid));

  const submit = async () => {
    // Validare: minim o intrare cu ore > 0 sau ore conduse > 0
    const valid = rows.filter((r) => (parseFloat(r.hours) || 0) > 0 || (parseFloat(r.hours_driven) || 0) > 0);
    if (valid.length === 0) {
      toast.error("Introdu cel puțin o alocare cu ore lucrate sau conduse");
      return;
    }
    for (const r of valid) {
      const h = parseFloat(r.hours) || 0;
      if (r.kind === "project" && !r.project_id) return toast.error("Alege proiectul pentru fiecare alocare de tip Proiect");
      if (r.kind === "dismantle" && !r.project_id) return toast.error("Alege proiectul demontat");
      if (r.kind === "regie" && !r.regie_id) return toast.error("Alege regia");
      if (r.kind === "other" && !(r.custom_label || "").trim()) return toast.error("Scrie unde a lucrat (Altul)");
      if (r.kind === "dismantle" && h > 4) {
        if (!window.confirm(`Demontajul are de obicei max 2h. Ai pus ${h}h. Continui?`)) return;
      }
    }
    setBusy(true);
    try {
      const payload = {
        date,
        entries: valid.map((r) => {
          const kind = r.kind;
          const e = {
            kind,
            hours: parseFloat(r.hours) || 0,
            hours_driven: parseFloat(r.hours_driven) || 0,
            note: r.note || null,
            is_dismantle: kind === "dismantle",
          };
          if (kind === "project" || kind === "dismantle") e.project_id = r.project_id || null;
          else if (kind === "regie") e.regie_id = r.regie_id || null;
          else if (kind === "other") e.label = (r.custom_label || "").trim();
          return e;
        }),
      };
      await api.put(`/persons/${person_id}/calendar/day`, payload);
      toast.success(`${valid.length} ${valid.length === 1 ? "alocare salvată" : "alocări salvate"} (total ${totalHours}h)`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally { setBusy(false); }
  };

  const removeAll = async () => {
    if (!window.confirm("Ștergi toate alocările pentru această zi?")) return;
    setBusy(true);
    try {
      await api.put(`/persons/${person_id}/calendar/day`, { date, entries: [] });
      toast.success("Alocări șterse");
      onSaved && onSaved();
    } catch { toast.error("Eroare"); } finally { setBusy(false); }
  };

  const projectOptions = useMemo(() => (projects || []).filter((p) => p.status !== "Demontat"), [projects]);
  // Pentru demontaj: arată proiectele 6.1 (de demontat) + 6 (în lucru, dacă end_date trecut) + 7 (deja demontat — pentru evidență retroactivă)
  const dismantleOptions = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return (projects || []).filter((p) => {
      const cat = String(p.category || "");
      if (cat === "7") return false; // după demontaj final nu mai logăm ore (totul e închis)
      if (cat === "6.1") return true;
      if (cat === "6" && p.end_date && p.end_date < today) return true;
      return false;
    });
  }, [projects]);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="log-hours-dialog">
        <DialogHeader>
          <DialogTitle>⏱️ Ore lucrate · {name}</DialogTitle>
          <p className="text-xs text-zinc-500 mt-1">
            Data: <strong>{date}</strong>
            <span className="ml-3">Total ore: <strong className="text-emerald-700">{totalHours}h</strong></span>
            {totalDriven > 0 && <span className="ml-2 text-blue-700">+ {totalDriven}h conduse</span>}
          </p>
        </DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          {rows.map((r, idx) => {
            const meta = KIND_META[r.kind] || KIND_META.project;
            return (
              <div key={r.rid} className={`rounded-md border p-2 ${meta.chip.replace("text-", "border-")}`} data-testid={`log-row-${idx}`}>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500 mr-1">#{idx + 1}</span>
                  {KIND_KEYS.map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => updateRow(r.rid, "kind", k)}
                      className={`text-[11px] px-2 py-1 rounded font-semibold border ${r.kind === k ? KIND_META[k].chip : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}
                      data-testid={`log-row-${idx}-kind-${k}`}
                    >
                      {KIND_META[k].label}
                    </button>
                  ))}
                  <div className="ml-auto flex items-center gap-1">
                    {rows.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeRow(r.rid)}
                        className="text-zinc-400 hover:text-red-600 p-1"
                        title="Șterge alocarea"
                        data-testid={`log-row-${idx}-remove`}
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>

                <div className="mt-2 grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-12 sm:col-span-6">
                    <Label className="text-[10px] text-zinc-500">
                      {r.kind === "dismantle" ? "Proiect demontat *" : r.kind === "project" ? "Proiect *" : r.kind === "regie" ? "Regie *" : r.kind === "magazie" ? "—" : "Descriere *"}
                    </Label>
                    {(r.kind === "project" || r.kind === "dismantle") && (
                      <Select value={r.project_id || "__none__"} onValueChange={(v) => updateRow(r.rid, "project_id", v === "__none__" ? "" : v)}>
                        <SelectTrigger className="mt-0.5 h-8" data-testid={`log-row-${idx}-project`}>
                          <SelectValue placeholder={r.kind === "dismantle" ? "Alege proiectul demontat..." : "Alege proiectul..."} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Niciunul —</SelectItem>
                          {(r.kind === "dismantle" ? dismantleOptions : projectOptions).map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name}{p.end_date && r.kind === "dismantle" ? ` (sf. ${fmtRoDate(p.end_date)})` : ""}
                            </SelectItem>
                          ))}
                          {r.kind === "dismantle" && dismantleOptions.length === 0 && (
                            <SelectItem value="__noop__" disabled>Niciun proiect disponibil pentru demontaj</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    )}
                    {r.kind === "regie" && (
                      <Select value={r.regie_id || "__none__"} onValueChange={(v) => updateRow(r.rid, "regie_id", v === "__none__" ? "" : v)}>
                        <SelectTrigger className="mt-0.5 h-8" data-testid={`log-row-${idx}-regie`}>
                          <SelectValue placeholder="Alege regia..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Niciuna —</SelectItem>
                          {(regii || []).filter((rr) => rr.active !== false).map((rr) => (
                            <SelectItem key={rr.id} value={rr.id}>{rr.name}{rr.firma ? ` · ${rr.firma}` : ""}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {r.kind === "magazie" && (
                      <div className="text-[11px] text-sky-700 bg-sky-50 border border-sky-200 rounded px-2 py-1.5">
                        Ore înregistrate la <strong>Magazie</strong>
                      </div>
                    )}
                    {r.kind === "other" && (
                      <Input
                        value={r.custom_label}
                        onChange={(e) => updateRow(r.rid, "custom_label", e.target.value)}
                        placeholder="ex: Curățenie hala, Transport materiale..."
                        className="mt-0.5 h-8"
                        data-testid={`log-row-${idx}-custom`}
                      />
                    )}
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <Label className="text-[10px] text-zinc-500">Ore lucrate {r.kind === "dismantle" && <span className="text-orange-600">(uzual ≤2h)</span>}</Label>
                    <Input
                      type="number"
                      step="0.5"
                      min={0}
                      max={24}
                      value={r.hours}
                      onChange={(e) => updateRow(r.rid, "hours", e.target.value)}
                      placeholder={r.kind === "dismantle" ? "1.5" : "8"}
                      className="mt-0.5 h-8"
                      data-testid={`log-row-${idx}-hours`}
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <Label className="text-[10px] text-zinc-500">🚗 Ore conduse</Label>
                    <Input
                      type="number"
                      step="0.5"
                      min={0}
                      max={24}
                      value={r.hours_driven}
                      onChange={(e) => updateRow(r.rid, "hours_driven", e.target.value)}
                      placeholder="0"
                      className="mt-0.5 h-8"
                      data-testid={`log-row-${idx}-driven`}
                    />
                  </div>
                  <div className="col-span-12">
                    <Input
                      value={r.note}
                      onChange={(e) => updateRow(r.rid, "note", e.target.value)}
                      placeholder="Notă (opțional)"
                      className="h-8 text-xs"
                      data-testid={`log-row-${idx}-note`}
                    />
                  </div>
                </div>
              </div>
            );
          })}

          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addRow("project")}
              className="text-blue-700 border-blue-300 hover:bg-blue-50"
              data-testid="log-add-row-project"
            >
              <Plus size={14} className="mr-1" /> Adaugă alocare
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addRow("dismantle")}
              className="text-orange-700 border-orange-300 hover:bg-orange-50"
              data-testid="log-add-row-dismantle"
              title="Demontajul nu se cumulează ca necesar — poate fi adăugat oricărei zile"
            >
              <Wrench size={14} className="mr-1" /> Adaugă demontaj
            </Button>
          </div>
        </div>

        <DialogFooter className="gap-2">
          {Array.isArray(fetchedDay) && fetchedDay.length > 0 && (
            <Button variant="outline" onClick={removeAll} disabled={busy} className="text-red-600 mr-auto" data-testid="log-hours-delete-all">
              Șterge tot
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={busy}>Anulează</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="log-hours-save">
            {busy ? "Se salvează…" : `Salvează ${totalHours > 0 ? `(${totalHours}h)` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
